import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TemplateApiService } from '../../templates/services/template-api.service';
import { TemplateCategoryApiService } from '../../templates/services/template-category-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  Template,
  TemplateCategory,
  TemplatePreviewResponse,
  TemplateStatus,
} from '../../templates/models/template.model';

/**
 * Modal para elegir una plantilla ACTIVA del business y aplicarla a una
 * campaña. Lista plantillas con filtros (categoría, búsqueda local) y un
 * panel lateral con preview en iframe. Emite la plantilla elegida.
 */
@Component({
  selector: 'app-template-picker-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './template-picker-modal.component.html',
})
export class TemplatePickerModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isOpen = false;
  @Output() selected = new EventEmitter<Template>();
  @Output() cancelled = new EventEmitter<void>();

  loading = false;
  error = '';

  templates: Template[] = [];
  filtered: Template[] = [];
  categories: TemplateCategory[] = [];

  searchTerm = '';
  filtroCategoryId = '';

  selectedTpl: Template | null = null;
  /** true mientras se resuelve el htmlContent completo de la plantilla al aplicar. */
  applying = false;

  // Preview interno (renderizado por el backend con samples del catálogo)
  previewLoading = false;
  previewSubject = '';
  previewHtml = '';
  previewError = '';

  /** Id del usuario logueado: distingue plantillas propias de públicas ajenas. */
  private currentUserId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TemplateApiService,
    private categoryApi: TemplateCategoryApiService,
    private auth: AuthService
  ) {
    this.currentUserId = this.auth.getCurrentUser()?.id ?? null;
  }

  esMia(t: Template): boolean {
    return this.currentUserId != null && t.createdBy === this.currentUserId;
  }

  visibilityLabel(t: Template): string {
    return t.isPublic ? 'Pública' : 'Personal';
  }

  visibilityBadgeClass(t: Template): string {
    // Sólido y de alto contraste para distinguir de un vistazo: verde = pública,
    // gris oscuro = personal (refuerzo con icono candado/personas en el HTML).
    return t.isPublic
      ? 'bg-success-500 text-white'
      : 'bg-gray-700 text-white dark:bg-gray-600';
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.cargar();
    } else if (changes['isOpen'] && !changes['isOpen'].currentValue) {
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.selectedTpl = null;
    this.searchTerm = '';
    this.filtroCategoryId = '';
    this.previewHtml = '';
    this.previewSubject = '';
    this.previewError = '';
  }

  private cargar(): void {
    this.loading = true;
    this.error = '';
    // Inc 3: solo plantillas ACTIVAS — las de Borrador aún no están listas para
    // usarse en un correo (y las Archivadas están retiradas).
    this.api
      .searchTemplates({ status: 'ACTIVE' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.templates = data ?? [];
          this.aplicarFiltros();
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'No se pudieron cargar las plantillas.';
          this.loading = false;
        },
      });

    this.categoryApi
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => (this.categories = data ?? []),
        error: () => {},
      });
  }

  aplicarFiltros(): void {
    // Refuerzo en cliente del filtro del backend: nunca ofrecer no-activas.
    let data = this.templates.filter((t) => t.status === 'ACTIVE');
    if (this.filtroCategoryId) {
      data = data.filter((t) => t.categoryId === this.filtroCategoryId);
    }
    const term = this.searchTerm.trim().toLowerCase();
    if (term) {
      data = data.filter(
        (t) =>
          t.name.toLowerCase().includes(term) ||
          (t.subject ?? '').toLowerCase().includes(term)
      );
    }
    this.filtered = data;
  }

  seleccionar(t: Template): void {
    this.selectedTpl = t;
    this.previewHtml = '';
    this.previewSubject = '';
    this.previewError = '';
    this.cargarPreview();
  }

  private cargarPreview(): void {
    if (!this.selectedTpl) return;
    const id = this.selectedTpl.idTemplate;
    this.previewLoading = true;
    this.api
      .preview(id, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: TemplatePreviewResponse) => {
          this.previewSubject = res.renderedSubject;
          this.previewHtml = res.renderedHtml;
          this.previewLoading = false;
        },
        error: () => {
          // Fallback a render literal sin interpolar samples
          this.previewSubject = this.selectedTpl?.subject ?? '';
          this.previewHtml = this.selectedTpl?.htmlContent ?? '';
          this.previewLoading = false;
        },
      });
  }

  hasMergeTags(t: Template): boolean {
    const re = /\{\{\s*[a-zA-Z_][\w.]*\s*\}\}/;
    return re.test(t.subject ?? '') || re.test(t.htmlContent ?? '');
  }

  statusLabel(s: TemplateStatus): string {
    switch (s) {
      case 'ACTIVE': return 'Activa';
      case 'DRAFT': return 'Borrador';
      case 'ARCHIVED': return 'Archivada';
    }
  }

  statusBadgeClass(s: TemplateStatus): string {
    switch (s) {
      case 'ACTIVE':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-300';
      case 'DRAFT':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  }

  categoryName(id?: string | null): string {
    if (!id) return 'Sin categoría';
    return this.categories.find((c) => c.idCategory === id)?.name ?? '—';
  }

  /**
   * Aplica la plantilla elegida. El listado (GET /api/templates) NO trae el
   * `htmlContent` por performance, así que el item de la lista tiene el cuerpo
   * vacío: emitirlo tal cual dejaba la campaña sin contenido y solo se veía el
   * chip "Plantilla: X" (Inc 25). Por eso pedimos la plantilla completa por id
   * antes de emitir, garantizando el HTML real (con merge tags sin resolver).
   */
  aplicar(): void {
    if (!this.selectedTpl || this.applying) return;
    const base = this.selectedTpl;
    // Si por algún motivo ya viene con contenido, no hace falta ir al backend.
    if ((base.htmlContent ?? '').trim().length > 0) {
      this.selected.emit(base);
      return;
    }
    this.applying = true;
    this.api
      .getTemplateById(base.idTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (full) => {
          this.applying = false;
          this.selected.emit(full);
        },
        error: () => {
          // Fallback: emitimos lo que tenemos para no bloquear al usuario.
          this.applying = false;
          this.selected.emit(base);
        },
      });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  trackById = (_: number, t: Template) => t.idTemplate;
  trackCatById = (_: number, c: TemplateCategory) => c.idCategory;
}
